import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plug, Loader2, Save } from "lucide-react";
import { getSettings, updateSettings } from "../../../services/dataService";
import { PageHeader } from "../../PageHeader";
import { StatusBadge } from "../../StatusBadge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { formatApiErrorDetail } from "../../../lib/api";
import { toast } from "sonner";

const FIELDS = [
  { name: "service_key", label: "Debit Order Service Key", placeholder: "Netcash NIF service key", type: "password" },
  { name: "account_service_key", label: "Account Service Key", placeholder: "Account service key", type: "password" },
  { name: "software_vendor_key", label: "Software Vendor Key", placeholder: "Vendor key" },
  { name: "api_endpoint", label: "API Endpoint", placeholder: "https://ws.netcash.co.za/NIWS/NIWS_NIF.svc" },
];

export function NetcashConnector() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings", "netcash"], queryFn: () => getSettings("netcash") });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const mut = useMutation({
    mutationFn: (payload) => updateSettings("netcash", payload),
    onSuccess: (res) => { qc.setQueryData(["settings", "netcash"], res); setForm(res); toast.success("Netcash settings saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div>
      <PageHeader title="Netcash Connector" icon={Plug} description="Service keys and endpoint configuration for submitting debit order batches to Netcash.">
        <StatusBadge status={form.connected ? "Processed & Cleared" : "Not Retried"} />
      </PageHeader>

      <div className="max-w-2xl rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-5" data-testid="netcash-connector-form">
        {FIELDS.map((f) => (
          <div className="space-y-2" key={f.name}>
            <Label>{f.label}</Label>
            <Input
              type={f.type || "text"}
              value={form[f.name] || ""}
              placeholder={f.placeholder}
              onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
              data-testid={`netcash-${f.name}`}
            />
          </div>
        ))}
        <Button onClick={() => mut.mutate(form)} disabled={mut.isPending} className="bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white font-semibold" data-testid="netcash-save">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Save Netcash Settings</>)}
        </Button>
      </div>
    </div>
  );
}
